import {CSSProperties, useEffect, useRef, useState} from 'react';
import {ReadOnlyIdView, ButtonView, ReadOnlyTextView, ReadOnlyText2Row2HeadersView} from '@vivli/shared/views';
import {upperSectionStyle} from './card-styles';
import { IUploadActionDetailDto } from '@vivli/features/dashboard/infrastructure/interface';
import {Color, Size} from '@vivli/shared/theme';
import { IUser } from '@vivli/shared/infrastructure/interface';
import { useRefState } from '@vivli/shared/infrastructure/hook';

interface UploadActionCardViewProps {
    dto: IUploadActionDetailDto;
    user?: IUser;
    onHeightChange?: (height: number) => void;
    handleActionCardButtonClick?: () => void;
    parentIsLoading?: boolean;
}

const upperSection5ColsStyle: CSSProperties = {
    ...upperSectionStyle,
    gridTemplateColumns: '.7fr .7fr .7fr 3.5fr auto',
    alignItems: 'center',
    height: '100%',
};

const upperSection5ColsStyleId: CSSProperties = {
    ...upperSection5ColsStyle,
    gridTemplateColumns: 'auto',
    height: '100%',
    minWidth: '120px',
    marginTop: '-11px',
};

const upperSection5ColsStyleMinWidth: CSSProperties = {
    ...upperSection5ColsStyle,
    minWidth: '150px',
};

const oneRowSmallComponentStyle: CSSProperties = {
    minWidth: '120px',
    height: '100%',
};

const cardContainerStyleUploadInProgress: CSSProperties = {
    display: 'grid',
    width: '100%',
    backgroundColor: Color.CARD_LIGHT_BLUE,
    borderTop: '2px solid rgb(176, 176, 176)',
    padding: Size.PADDING,
    gap: '8px 0',
};

export const UploadActionCardView = ({
    dto,
    user,
    onHeightChange,
    handleActionCardButtonClick,
    parentIsLoading,
}: UploadActionCardViewProps) => {
    const defaultRowHeight = 110;
    const [showUploadRef, showUploadSection, setShowUploadSection] = useRefState(false);
    const currentRowHeightRef = useRef(defaultRowHeight);
    const [dataPackageReady, setDataPackageReady] = useState(false);
    const uploadDivRef = useRef<HTMLDivElement>();

    const updateCurrentRowHeight = () => {
        const currentHeight = uploadDivRef?.current?.getBoundingClientRect().height;
        const height = showUploadRef.current ? currentHeight + defaultRowHeight + 440 : defaultRowHeight;
        if (height !== currentRowHeightRef.current) {
            currentRowHeightRef.current = height;
            onHeightChange && onHeightChange(height);
        }
    };

    const handleButtonClick = (e) => {
        e.stopPropagation();
        handleActionCardButtonClick();
        setShowUploadSection(true);
    };

    useEffect(() => {
        if (showUploadSection) {
            // when showing the uploader move the update row height call to the end of the stack
            // and give it a half second extra to ensure the component has fully rendered
            // so that the height is correct when the upload section is shown
            setTimeout(() => {
                updateCurrentRowHeight();
            }, 500);
        } else {
            // if hiding the uploader, close it immediately, no need to wait on rendering
            updateCurrentRowHeight();
        }
    }, [showUploadSection]);

    useEffect(() => {
        if (parentIsLoading === null) {
            return;
        }
    }, [parentIsLoading]);

    return (
        <div style={cardContainerStyleUploadInProgress}>
            <div style={upperSection5ColsStyle}>
                <div style={{ minWidth: '150px', marginTop: '-11px' }}>
                    <ReadOnlyIdView idContent={dto.studyId} idHeader={'Study ID'} substituteForEmpty={true}></ReadOnlyIdView>
                </div>
                <div style={{ minWidth: '170px', marginTop: '-11px' }}>
                    <ReadOnlyIdView idContent={dto.uniqueId} idHeader={'Sponsor ID'}></ReadOnlyIdView>
                </div>
                <div style={upperSection5ColsStyleId}>
                    <ReadOnlyTextView textContent={dto.dataRequestId} textHeader={'Data Request ID'}></ReadOnlyTextView>
                </div>
                <div style={upperSection5ColsStyleMinWidth}>
                    <ReadOnlyText2Row2HeadersView
                        textContent={dto.requestType}
                        textContent2={dto.availability}
                        textHeader={'Settings'}
                        showButton={false} //hardcoded to false for right now - original was: {dto.userCanEditSettings}
                        textHeader2={'Edit'}
                    ></ReadOnlyText2Row2HeadersView>
                </div>
                <div style={{ justifySelf: 'end' }}>
                    <ButtonView title={dto.actionText} theme={'white-on-lightblue'} onClick={handleButtonClick} isLoading={parentIsLoading}>
                        {dto.actionText}
                    </ButtonView>
                </div>
            </div>
        </div>
    );
};
